import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const faqs = [
  {
    question: 'How do I create an account?',
    answer:
      'You can sign up using your email address or use Sign in with Apple for a quick, privacy-focused login. Simply open the app and tap "Get Started" to begin.',
  },
  {
    question: 'How do I add a baby?',
    answer:
      'Go to Settings > Babies > + Add. Enter your baby\'s name, date of birth, and gender to create their profile. You can add multiple babies depending on your subscription plan.',
  },
  {
    question: 'How do I share with my partner?',
    answer:
      'Navigate to Settings > Caregivers, enter your partner\'s email address, and tap Invite. They\'ll receive an invitation to join and access your baby\'s data.',
  },
  {
    question: 'How do I track a feed, sleep, or nappy change?',
    answer:
      'Tap the relevant tab in the sidebar (Feed, Sleep, or Nappy) or use the quick actions on the dashboard. Each tracker lets you log the time, duration, and relevant details.',
  },
  {
    question: 'What\'s included in the free plan?',
    answer:
      'The free plan includes tracking for 1 baby, up to 2 caregivers, 7 days of history, and basic tracking features including feeds, sleep, nappy changes, and activities.',
  },
  {
    question: 'What do Plus and Premium include?',
    answer:
      'Plus (£4.99/mo or £49.99/yr) includes up to 10 babies, unlimited history, growth charts, CSV export, and vaccination reminders. Premium (£9.99/mo or £99.99/yr) includes everything in Plus plus AI-powered insights, paediatrician-ready PDF reports, FHIR export, and voice logging.',
  },
  {
    question: 'How do I cancel my subscription?',
    answer:
      'For iOS subscriptions: go to your device Settings > Apple ID > Subscriptions and cancel from there. For web subscriptions: manage your subscription through the subscription page within the app.',
  },
  {
    question: 'How do I delete my account?',
    answer:
      'To delete your account and all associated data, please contact us at support@wolftechnologies.co.uk. We will process your request and confirm once all data has been removed.',
  },
  {
    question: 'Is my data secure?',
    answer:
      'Yes. All data is stored securely on AWS infrastructure in the UK (London, eu-west-2 region). Data is encrypted both in transit and at rest. We do not sell or share your data with advertisers.',
  },
];

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className={`faq__item ${isOpen ? 'faq__item--open' : ''}`} onClick={onClick}>
    <div className="faq__question">
      <h3>{question}</h3>
      <svg
        className="faq__chevron"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M6 9l6 6 6-6" />
      </svg>
    </div>
    {isOpen && (
      <div className="faq__answer">
        <p>{answer}</p>
      </div>
    )}
  </div>
);

const BabyTrackSupport = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Support</span>
          <h1 className="legal-page__title">BabyTrack Support</h1>
          <p className="legal-page__subtitle">
            Get help with BabyTrack - Love your Baby
          </p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          {/* Contact Section */}
          <section className="support-contact">
            <div className="support-contact__card">
              <div className="support-contact__icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <div>
                <h3>Need help?</h3>
                <p>
                  Contact us at{' '}
                  <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
                </p>
                <p className="support-contact__response">
                  We typically respond within 24 hours.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="legal-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq__list">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() => toggleFAQ(index)}
                />
              ))}
            </div>
          </section>

          {/* App Info */}
          <section className="legal-section">
            <h2>App Information</h2>
            <div className="support-info__grid">
              <div className="support-info__item">
                <span className="support-info__label">App</span>
                <span className="support-info__value">BabyTrack - Love your Baby</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Version</span>
                <span className="support-info__value">1.0</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Developer</span>
                <span className="support-info__value">Wolf Technologies Ltd</span>
              </div>
              <div className="support-info__item">
                <span className="support-info__label">Platform</span>
                <span className="support-info__value">iOS</span>
              </div>
            </div>
          </section>

          {/* Links */}
          <section className="legal-section">
            <h2>Useful Links</h2>
            <div className="support-links">
              <Link to="/babytrack/privacy" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0110 0v4" />
                </svg>
                Privacy Policy
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </Link>
              <a href="/" className="support-links__item">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                WolfTechnologies Home
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default BabyTrackSupport;
